import type { createSearchAutosuggestCore } from "~/lib/search/create-search-autosuggest-core.svelte";

type SearchAutosuggestCore = ReturnType<typeof createSearchAutosuggestCore>;

/** Read through getters: both shells pass props in (state_referenced_locally). */
export interface SearchShellOptions {
  readonly core: SearchAutosuggestCore;
  readonly onSubmit: (query: string) => void;
  /**
   * Escape. The shells stop the event differently — the mobile sheet has to let the
   * press that clears the field travel on to SlideOverDialog — so calling
   * `core.escape()` and acting on its answer is theirs.
   */
  onEscape: (event: KeyboardEvent) => void;
}

/**
 * Everything the two search shells do identically: split `core.items` into the columns
 * they both lay out, and handle typing, Enter and arrow keys. Only the layout and the
 * Escape policy differ between them.
 */
export function createSearchShell(options: SearchShellOptions) {
  const items = $derived(options.core.items);
  const productItems = $derived(items.filter((i) => i.kind === "product"));
  const suggestionItems = $derived(items.filter((i) => i.kind === "suggestion"));
  const showAllItem = $derived(items.find((i) => i.kind === "showAll"));

  function selectTerm(term: string) {
    options.core.setQuery(term);
    options.onSubmit(term);
  }

  function activateHighlighted(): boolean {
    const item = options.core.highlightedItem;
    if (!item) return false;
    if (item.kind === "suggestion") {
      selectTerm(item.term);
    } else {
      window.location.href = item.href;
    }
    return true;
  }

  // The panel is laid out on two axes — a row of cards, a list of terms — over one
  // linear item order, so both axes walk that order. Left/Right only once something is
  // highlighted: until then they still have to move the text caret.
  function arrowDelta(event: KeyboardEvent): 1 | -1 | undefined {
    // Alt+Arrow belongs to the popup, not the highlight (WAI-ARIA APG combobox).
    if (event.altKey) return undefined;
    if (event.key === "ArrowDown") return 1;
    if (event.key === "ArrowUp") return -1;
    if (options.core.highlightedIndex < 0) return undefined;
    if (event.key === "ArrowRight") return 1;
    if (event.key === "ArrowLeft") return -1;
    return undefined;
  }

  return {
    selectTerm,

    get productItems() {
      return productItems;
    },
    get suggestionItems() {
      return suggestionItems;
    },
    get showAllItem() {
      return showAllItem;
    },

    /**
     * The panel always holds something — skeletons, results, or a no-results line — but
     * only the results state has a listbox, and aria-expanded tracks the listbox rather
     * than the panel: an expanded combobox must have aria-controls
     * (aria-required-attr), and there is nothing to point it at while loading or empty.
     */
    get listboxVisible(): boolean {
      const core = options.core;
      return core.panelVisible && !core.isFetching && !core.isEmpty;
    },

    /**
     * Text for the shell's permanently mounted live region. A `role="status"` inserted
     * into the DOM together with its content is not reliably announced, so the region
     * outlives the panel and only its text changes.
     */
    get status(): string {
      const core = options.core;
      if (!core.panelVisible) return "";
      if (core.isFetching) return "Loading search suggestions";
      if (core.isEmpty) return `No results for ${core.displayValue}`;
      return `${items.length} suggestions available`;
    },

    handleInput(event: Event) {
      const value = (event.target as HTMLInputElement).value;
      options.core.setQuery(value);
    },

    handleKeydown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        options.onEscape(event);
        return;
      }
      if (event.key === "Enter") {
        if (!activateHighlighted()) options.onSubmit(options.core.displayValue);
        return;
      }
      if (!options.core.panelVisible) return;
      const delta = arrowDelta(event);
      if (delta) {
        event.preventDefault();
        options.core.moveHighlight(delta);
      }
    },
  };
}
